import { Game } from '../../../core/game';
import { deserializeSave, type SaveData } from '../../../core/save';
import { writeSave } from '../../../core/storage';
import { formatGold } from '../../format';

/**
 * Cœur partagé de l'import de partie (A96-3) : utilisé par la modale
 * « Sauvegarde » des Réglages (`saveTransfer.ts`, partie **non** marquée) et
 * par la section « Sauvegarde » du panneau de debug (`ui/debug/panel.ts`, partie
 * marquée debug, A87). Ne touche pas au DOM : tout se teste sous Node.
 *
 * La différence entre les deux usages tient à un seul drapeau, `markDebug` :
 * une partie importée depuis les Réglages garde le marquage qu'elle portait
 * (`meta.tainted` d'une partie déjà truquée est conservé, on ne « blanchit »
 * rien), sans en ajouter ; depuis le debug, elle est toujours marquée.
 */

export interface PrepareOptions {
  /** Vrai dans le panneau de debug : la partie importée est marquée « debug ». */
  markDebug: boolean;
}

/** Marque une sauvegarde déjà sérialisée (repli sans `Game.importSave`). */
function markData(save: SaveData): void {
  if (save.meta && typeof save.meta === 'object') save.meta.tainted = true;
}

/**
 * Valide un texte de partie (JSON exporté) et renvoie la sauvegarde
 * normalisée, prête à écrire. **Jette** une `Error` au message français
 * affichable tel quel : texte vide, JSON illisible, ce n'est pas une partie,
 * version trop ancienne.
 */
export function prepareImport(raw: string, opts: PrepareOptions): SaveData {
  const text = typeof raw === 'string' ? raw.trim() : '';
  if (text === '') throw new Error('Rien à importer.');
  const Ctor = Game as unknown as { importSave?: (json: string) => Game };
  if (typeof Ctor.importSave === 'function') {
    const imported = Ctor.importSave(text);
    if (opts.markDebug) {
      (imported as unknown as { markTainted?: () => void }).markTainted?.();
    }
    const save = imported.toSave();
    if (opts.markDebug) markData(save);
    return save;
  }
  // Cœur sans `Game.importSave` (très ancien) : validation par le désérialiseur.
  const save = deserializeSave(text);
  if (!save) throw new Error('Sauvegarde invalide : JSON illisible.');
  if (opts.markDebug) markData(save);
  return save;
}

/** Écrit la partie importée dans le stockage du navigateur. */
export function commitImport(save: SaveData): boolean {
  return writeSave(save);
}

/** Délai entre l'annonce « rechargement… » et le rechargement lui-même. */
export const RELOAD_DELAY_MS = 300;

/**
 * Écrit la partie, l'annonce, puis recharge la page. La sauvegarde est
 * **réécrite juste avant** le rechargement : l'autosave débouncée du jeu
 * (400 ms) pourrait, dans l'intervalle, avoir remis l'ancienne partie.
 * Renvoie faux si le stockage refuse l'écriture (rien n'est alors rechargé).
 */
export function commitAndReload(save: SaveData, reload: () => void = () => window.location.reload()): boolean {
  if (!commitImport(save)) return false;
  window.setTimeout(() => {
    commitImport(save);
    reload();
  }, RELOAD_DELAY_MS);
  return true;
}

// ─────────────────────────────────────────────────────────────── Formats

const pad2 = (n: number): string => String(n).padStart(2, '0');

/** Date locale `AAAA-MM-JJ`. */
export function isoDay(date: Date): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

/** Nom du fichier téléchargé : `diceordie-AAAA-MM-JJ.json`. */
export function saveFileName(date: Date): string {
  return `diceordie-${isoDay(date)}.json`;
}

/** Date et heure lisibles d'une partie : « 15/10/2026 à 14:03 ». */
export function saveStamp(date: Date): string {
  return `${pad2(date.getDate())}/${pad2(date.getMonth() + 1)}/${date.getFullYear()} à ${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

/** Ligne de rappel du volet Exporter : date de la partie et niveau atteint. */
export function exportSummary(date: Date, level: number, gold: number): string {
  return `Partie du ${saveStamp(date)} · niveau ${level} · ${formatGold(gold)} or`;
}

/** Texte de la confirmation avant de remplacer la partie en cours. */
export function replaceConfirmText(level: number, gold: number): string {
  return `La partie actuelle (niveau ${level}, ${formatGold(gold)} or) sera remplacée. Continuer ?`;
}

/** Résumé de la partie qui va arriver (affiché sous la confirmation). */
export function incomingSummary(save: SaveData): string {
  return `Partie importée : niveau ${save.run.level}, ${formatGold(save.meta.gold)} or.`;
}
